import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { getTaskList } from "api";
import { TaskListFilterOption } from "components";
import { DateHelper, TaskStatusEnum } from "config";
import { TaskListSectionType } from "pages/user-portal/employee/homepage";
import { store } from "store";

import { EmployeeHomepageState } from "./types";
import {
  getFirstDayOfNextWeekStartOfDayUTC,
  getLastDayOfWeekEndOfDayUTC,
  getTaskListFilters,
  getTodayEndOfDayUTC,
  getTodayStartOfDayUTC,
  getTomorrowStartOfDayUTC,
  getYesterdayEndOfDayUTC,
} from "./utils";

const initialState: EmployeeHomepageState = {
  [TaskListSectionType.DUE_TODAY]: {
    taskList: [],
    loading: false,
  },
  [TaskListSectionType.DUE_END_OF_WEEK]: {
    taskList: [],
    loading: false,
  },
  [TaskListSectionType.OTHER]: {
    taskList: [],
    loading: false,
  },
};

const getFilteredFilters = (
  filters: Array<Record<string, any>>
): Array<Record<string, any>> => {
  const filteredFilters = [] as Array<Record<string, any>>;
  filters.forEach((item: Record<string, any>) => {
    for (const [key, value] of Object.entries(item)) {
      if (
        [...Object.values(TaskListFilterOption), "taskType"].includes(
          key as TaskListFilterOption
        )
      ) {
        filteredFilters.push({ [key]: value });
      }
    }
  });

  return filteredFilters;
};

export const fetchTaskList = {
  [TaskListSectionType.DUE_TODAY]: createAsyncThunk(
    `employeeHomepage/fetchTaskList${TaskListSectionType.DUE_TODAY}`,
    async () => {
      const userFilters = store.getState().FiltersReducer?.filters || [];
      const filteredUserFilters = getFilteredFilters(userFilters);
      const dueTodayVars = getTaskListFilters({
        fromDate: getTodayStartOfDayUTC(),
        toDate: getTodayEndOfDayUTC(),
      });
      const notCompletedVars = getTaskListFilters({
        fromDate: DateHelper.getMinDate(),
        toDate: getYesterdayEndOfDayUTC(),
        neStatus: TaskStatusEnum.COMPLETED,
      });
      const response = await Promise.all([
        getTaskList([...notCompletedVars, ...filteredUserFilters]),
        getTaskList([...dueTodayVars, ...filteredUserFilters]),
      ]);
      return response.reduce((acc, item) => {
        acc.push(...item);

        return acc;
      }, []);
    }
  ),
  [TaskListSectionType.DUE_END_OF_WEEK]: createAsyncThunk(
    `employeeHomepage/fetchTaskList${TaskListSectionType.DUE_END_OF_WEEK}`,
    async () => {
      const userFilters = store.getState().FiltersReducer?.filters || [];
      const filteredUserFilters = getFilteredFilters(userFilters);
      const vars = getTaskListFilters({
        fromDate: getTomorrowStartOfDayUTC(),
        toDate: getLastDayOfWeekEndOfDayUTC(),
      });
      const response = await getTaskList([...vars, ...filteredUserFilters]);
      return response;
    }
  ),
  [TaskListSectionType.OTHER]: createAsyncThunk(
    `employeeHomepage/fetchTaskList${TaskListSectionType.OTHER}`,
    async () => {
      const userFilters = store.getState().FiltersReducer?.filters || [];
      const filteredUserFilters = getFilteredFilters(userFilters);
      const vars = getTaskListFilters({
        fromDate: getFirstDayOfNextWeekStartOfDayUTC(),
        toDate: DateHelper.getMaxDate(),
      });
      const response = await getTaskList([...vars, ...filteredUserFilters]);
      return response;
    }
  ),
};

const employeeHomepageSlice = createSlice({
  name: "employeeHomepage",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    Object.entries(fetchTaskList).forEach(([key, value]) => {
      builder.addCase(value.fulfilled, (state, action) => {
        if (!action.payload) return;
        return {
          ...state,
          [key]: {
            taskList: action.payload,
            loading: false,
          },
        };
      });
      builder.addCase(value.pending, (state) => {
        return {
          ...state,
          [key]: {
            ...{
              ...state[key as TaskListSectionType],
              loading: true,
            },
          },
        };
      });
    });
  },
});

export default employeeHomepageSlice.reducer;
